import { CustomGeojson3d, FlightLayerFacade } from '@fe/flight-layer';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { DrawingObjectsLayer } from '../../../components/drawing-object/drawing-object-layer';
import { RouteAnalyzerLayer } from '../../../components/route-analyzer/layers/route-analyzer-layer';
import { AirportMonitorStatusLayer } from '../../../layers/airport-monitor-status-layer';
import { DiversionStatusLayer } from '../../../layers/diversion-status-layer';
import { NavDataPlugin } from '../../../plugins/nav-data-plugin';
import { FeWeatherPlugin } from '../../../services/fe-weather-plugin';
import { MapInteractionService } from '../../../services/map-interaction-service';
import { timeService } from '../../../services/time/time-service';
import { FeMap } from '../../components/map/fe-map';
import { useAppSelector } from '../../hooks/store-hooks';
import WorkspaceItemContext, { WorkspaceItemContextObject } from './workspace-item-context';
import { WorkspaceManagementContext } from './workspace-management-context';

function newContext(workspaceId: number): WorkspaceItemContextObject {
    const weatherService = new FeWeatherPlugin(workspaceId, timeService);
    const navDataPlugin = new NavDataPlugin(workspaceId);
    const flightLayerFacade = new FlightLayerFacade(workspaceId);
    // TODO This will add the last FlightFacade instance to weather service. Make it better.
    weatherService.flightLayerFacade = flightLayerFacade;
    return {
        id: workspaceId,
        weatherService: weatherService,
        navDataPlugin: navDataPlugin,
        interactionService: new MapInteractionService(weatherService, navDataPlugin, flightLayerFacade),
        routeAnalyzerLayer: new RouteAnalyzerLayer(),
        drawingObjectsLayer: new DrawingObjectsLayer(),
        airportMonitorStatusLayer: new AirportMonitorStatusLayer(),
        diversionLayer: new DiversionStatusLayer(),
        customGeojsonPlugin: new CustomGeojson3d(flightLayerFacade.deckPlugin),
        flightLayerFacade: flightLayerFacade,
        map: null
    };
}

export function WorkspacesProvider(props: { children?: ReactNode }) {
    const workspaceIds = useAppSelector(
        (state) => state.workspace.items.map((item) => item.id),
        // Check if same order
        (p, n) => p.length === n.length && p.every((id, index) => id === n[index])
    );
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const [workspaceContexts, setWorkspaceContexts] = useState<WorkspaceItemContextObject[]>(() => workspaceIds.map(newContext));

    const setMap = useCallback((map: FeMap | null, workspaceId: number) => {
        setWorkspaceContexts((previous) => {
            const _new = [...previous];
            const ws = _new[workspaceId];
            if (ws) {
                _new[workspaceId] = setMapInWorkspace(map, ws);
            }

            return _new;
        });
    }, []);

    useEffect(() => {
        setWorkspaceContexts((p) => {
            if (workspaceIds.length === p.length) {
                return p;
            }

            if (p.length > workspaceIds.length) {
                return p.slice(0, workspaceIds.length);
            }

            return [...p, ...workspaceIds.slice(p.length).map(newContext)];
        });
    }, [workspaceIds]);

    const contextValue = useMemo(
        () => ({ setMap, items: workspaceContexts }) satisfies WorkspaceManagementContext,
        [workspaceContexts, setMap]
    );

    return (
        /* This context always refers to the currently active workspace map, this way all the components the use this context don't need to know whether they are being used multiple times in each workspace map or once and connected to the active workspace map */
        <WorkspaceManagementContext.Provider value={contextValue}>
            <WorkspaceItemContext.Provider value={workspaceContexts[focusedWorkspaceId]}>{props.children}</WorkspaceItemContext.Provider>
        </WorkspaceManagementContext.Provider>
    );
}

function setMapInWorkspace(map: FeMap | null, workspace: WorkspaceItemContextObject): WorkspaceItemContextObject {
    // If there was a map before, remove plugins
    if (workspace.map) {
        workspace.map
            .removePlugin(workspace.weatherService)
            .removePlugin(workspace.interactionService)
            .removePlugin(workspace.routeAnalyzerLayer)
            .removePlugin(workspace.drawingObjectsLayer)
            .removePlugin(workspace.airportMonitorStatusLayer)
            .removePlugin(workspace.diversionLayer)
            .removePlugin(workspace.customGeojsonPlugin)
            .removePlugin(workspace.navDataPlugin);
    }

    if (!map) {
        return {
            ...workspace,
            map: null
        };
    } else {
        const result = { ...workspace, map };

        map.addPlugin(workspace.weatherService)
            .addPlugin(workspace.interactionService)
            .addPlugin(workspace.routeAnalyzerLayer)
            .addPlugin(workspace.drawingObjectsLayer)
            .addPlugin(workspace.airportMonitorStatusLayer)
            .addPlugin(workspace.diversionLayer)
            .addPlugin(workspace.customGeojsonPlugin)
            .addPlugin(workspace.navDataPlugin);

        return result;
    }
}
